'use client';

import { useState, useEffect, useMemo } from 'react';
import { useJumpLinks, MAX_SIDEBAR_WIDTH } from './hooks';
import { JumpLinksToggle } from './JumpLinksToggle';
import { JumpLinksList } from './JumpLinksList';

interface JumpLinksProps {
  title?: string;
  minHeadingLevel?: number;
  maxHeadingLevel?: number;
}

// base sizes (before scaling)
const BASE_FONT_SIZE = 16;
const BASE_ICON_SIZE = 24;
const BASE_PADDING = 16;
const BASE_GAP = 16;
const BASE_INDENT = 8;
const BASE_TEXT_PADDING = 39;
const BASE_TRACK_LEFT = 9;

export const JumpLinks = ({ title = 'Jump to section', minHeadingLevel = 1, maxHeadingLevel = 6 }: JumpLinksProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    headings,
    activeId,
    isFloating,
    isSticky,
    sidebarLeft,
    sidebarWidth,
    fontScale,
    initialTop,
    stickyTop,
    listRef,
    scrollToHeading,
    scrollActiveIntoView,
  } = useJumpLinks({ minHeadingLevel, maxHeadingLevel });

  // auto-scroll when active changes
  useEffect(() => {
    scrollActiveIntoView(isOpen);
  }, [activeId, isOpen, scrollActiveIntoView]);

  // memoize scaled values to avoid recalculating on every render
  const scaledValues = useMemo(() => {
    const scale = (base: number, round = true) => {
      const scaled = isFloating ? base * fontScale : base;
      return round ? Math.round(scaled) : scaled;
    };

    const fontSize = scale(BASE_FONT_SIZE, false);
    const iconSize = scale(BASE_ICON_SIZE);
    const padding = scale(BASE_PADDING);
    const gap = scale(BASE_GAP);
    const indentMultiplier = scale(BASE_INDENT, false);
    const textPadding = scale(BASE_TEXT_PADDING);
    const trackLeft = scale(BASE_TRACK_LEFT);

    return {
      fontSize,
      iconSize,
      padding,
      gap,
      indentMultiplier,
      textPadding,
      trackLeft,
      styles: {
        container: { gap: `${gap}px`, padding: `${padding}px` },
        button: { gap: `${gap}px` },
        title: { fontSize: `${fontSize}px` },
        track: { left: `${trackLeft}px` },
      },
    };
  }, [isFloating, fontScale]);

  // don't render if no headings found
  if (headings.length === 0) return null;

  const minLevel = Math.min(...headings.map((h) => h.level));

  const cardContent = (
    <div className="border-dark-200 overflow-clip border bg-white">
      <div className="flex flex-col" style={scaledValues.styles.container}>
        <JumpLinksToggle
          title={title}
          isOpen={isOpen}
          onToggle={() => setIsOpen(!isOpen)}
          scaledIconSize={scaledValues.iconSize}
          scaledFontSize={scaledValues.fontSize}
          buttonStyle={scaledValues.styles.button}
          titleStyle={scaledValues.styles.title}
        />

        {isOpen && (
          <JumpLinksList
            ref={listRef}
            headings={headings}
            activeId={activeId}
            minLevel={minLevel}
            scaledIndentMultiplier={scaledValues.indentMultiplier}
            scaledTextPadding={scaledValues.textPadding}
            scaledFontSize={scaledValues.fontSize}
            trackStyle={scaledValues.styles.track}
            onScrollTo={scrollToHeading}
          />
        )}
      </div>
    </div>
  );

  // desktop with enough space: sticky sidebar on the left
  if (isFloating) {
    return (
      <div
        className="z-40"
        style={{
          width: `${sidebarWidth}px`,
          position: isSticky ? 'fixed' : 'absolute',
          top: isSticky ? `${stickyTop}px` : `${initialTop}px`,
          left: `${sidebarLeft}px`,
        }}
      >
        {cardContent}
      </div>
    );
  }

  // mobile or not enough space: inline at top of content
  return (
    <div className="tc-container mb-6">
      <div style={{ maxWidth: `${MAX_SIDEBAR_WIDTH}px` }}>{cardContent}</div>
    </div>
  );
};
